import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  endCommentThreadMeeting,
  isActiveCommentThreadMeeting,
  isGoogleCalendarConnected,
  startCommentThreadMeeting,
  subscribeCommentThreadMeeting,
  type CommentThreadMeeting,
} from '../utils/commentThreadMeetingService';

export function useCommentThreadMeeting(params: {
  enabled: boolean;
  threadKey: string;
  user: { id: string; name?: string | null; email?: string | null } | null;
  meetingTitle: string;
  meetingDescription?: string;
  projectId?: string;
}) {
  const { enabled, threadKey, user, meetingTitle, meetingDescription, projectId } = params;
  const [meeting, setMeeting] = useState<CommentThreadMeeting | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calendarConnected, setCalendarConnected] = useState<boolean | null>(null);

  useEffect(() => {
    if (!enabled || !threadKey) {
      setMeeting(null);
      return;
    }
    return subscribeCommentThreadMeeting(threadKey, setMeeting);
  }, [enabled, threadKey]);

  useEffect(() => {
    if (!enabled || !user?.id) {
      setCalendarConnected(null);
      return;
    }
    let cancelled = false;
    void isGoogleCalendarConnected(user.id).then((ok) => {
      if (!cancelled) setCalendarConnected(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, user?.id]);

  const active = useMemo(
    () => isActiveCommentThreadMeeting(meeting),
    [meeting],
  );

  const isHost = !!user?.id && meeting?.hostUid === user.id;

  const startMeeting = useCallback(async () => {
    if (!enabled || !user?.id || !threadKey) return;
    setLoading(true);
    setError(null);
    try {
      const connected = await isGoogleCalendarConnected(user.id);
      setCalendarConnected(connected);
      if (!connected) {
        throw new Error('Connect Google Calendar in Settings or the Calendar page to start a Meet.');
      }
      await startCommentThreadMeeting({
        threadKey,
        hostUid: user.id,
        hostName: user.name || user.email || 'Someone',
        title: meetingTitle,
        description: meetingDescription,
        projectId,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not start meeting';
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [
    enabled,
    user,
    threadKey,
    meetingTitle,
    meetingDescription,
    projectId,
  ]);

  const endMeeting = useCallback(async () => {
    if (!user?.id || !threadKey || !isHost) return;
    setLoading(true);
    setError(null);
    try {
      await endCommentThreadMeeting(threadKey, user.id);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not end meeting';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [user?.id, threadKey, isHost]);

  const refreshCalendarConnected = useCallback(async () => {
    if (!user?.id) return false;
    const ok = await isGoogleCalendarConnected(user.id);
    setCalendarConnected(ok);
    return ok;
  }, [user?.id]);

  return {
    meeting: active ? meeting : null,
    active,
    isHost,
    loading,
    error,
    calendarConnected,
    startMeeting,
    endMeeting,
    refreshCalendarConnected,
  };
}
